import { useState, useEffect, useRef } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { Badge } from '@/shared/components/ui/badge';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/shared/utils/queryClient';
import { useBranding } from '@/shared/contexts/BrandingContext';
import { 
  MessageCircle, 
  Send, 
  X, 
  Bot, 
  User, 
  AlertCircle,
  Phone,
  Mail,
  Minus
} from 'lucide-react';
import { useToast } from '@/shared/hooks/use-toast';
import { cn } from '@/shared/utils/utils';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  category?: string;
  needsEscalation?: boolean;
}

interface ChatWidgetProps {
  customerId?: string;
  className?: string;
  defaultOpen?: boolean;
  position?: 'bottom-right' | 'bottom-left' | 'inline';
}

export default function CustomerSupportChat({ 
  customerId, 
  className = '',
  defaultOpen = false,
  position = 'bottom-right' 
}: ChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const { currentBrand, isAegis360, isGreenshield } = useBranding();
  const { toast } = useToast();

  // Check if user has access to AI chat
  const { data: accessData, isLoading: isCheckingAccess } = useQuery({
    queryKey: ['/api/support/access'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/support/access');
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1
  });

  // Brand-specific styling
  const brandColors = isAegis360 ? {
    primary: 'bg-blue-600 hover:bg-blue-700',
    secondary: 'bg-blue-100 text-blue-800',
    accent: 'border-blue-200',
    text: 'text-blue-900'
  } : {
    primary: 'bg-emerald-600 hover:bg-emerald-700', 
    secondary: 'bg-emerald-100 text-emerald-800',
    accent: 'border-emerald-200',
    text: 'text-emerald-900'
  };

  const brandInfo = {
    name: currentBrand.companyName,
    supportText: isAegis360 
      ? 'Get instant help with your software questions'
      : 'Get immediate pest control assistance',
    welcomeMessage: isAegis360
      ? "Hi! I'm your Aegis360 support assistant. How can I help with your software today?"
      : "Hi! I'm here to help with your pest control needs. What can I assist you with today?"
  };

  // Send message to customer support API
  const sendMessageMutation = useMutation({
    mutationFn: async (message: string) => {
      const response = await apiRequest('POST', '/api/support/chat', {
        message,
        sessionId,
        customerId,
        brand: currentBrand.type
      });
      return response.json();
    },
    onSuccess: (response) => {
      // Add AI response to messages
      const aiMessage: ChatMessage = {
        id: `ai_${Date.now()}`,
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
        category: response.category,
        needsEscalation: response.needsEscalation
      };
      
      setMessages(prev => [...prev, aiMessage]);
      setIsTyping(false);

      // Handle escalation
      if (response.needsEscalation) {
        toast({
          title: "Connecting to Human Agent",
          description: response.escalationReason || "A specialist will assist you shortly.",
          variant: "default",
        });
      }
    },
    onError: (error: any) => {
      setIsTyping(false);
      
      // Add error message
      const errorMessage: ChatMessage = {
        id: `error_${Date.now()}`,
        role: 'assistant',
        content: "I apologize, but I'm experiencing technical difficulties. Let me connect you with a human agent who can help you right away.",
        timestamp: new Date(),
        needsEscalation: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
      
      toast({
        title: "Connection Issue",
        description: "Connecting you to a human agent for assistance.",
        variant: "destructive",
      });
    }
  });

  const sendMessage = async () => {
    if (!inputMessage.trim()) {
      return;
    }
    
    // Add user message to chat
    const userMessage: ChatMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage.trim();
    setInputMessage('');
    setIsTyping(true);
    
    // Send to API
    sendMessageMutation.mutate(messageToSend);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Add welcome message when chat opens for first time
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage: ChatMessage = {
        id: 'welcome',
        role: 'assistant',
        content: brandInfo.welcomeMessage,
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen, messages.length, brandInfo.welcomeMessage]);

  // Don't render anything while checking access
  if (isCheckingAccess) {
    return null;
  }

  // Don't render chat widget if user doesn't have access
  if (accessData && !accessData.hasAccess) {
    // Show a subtle indicator for internal users that chat is disabled
    if (accessData.isInternal && accessData.reason) {
      return (
        <div className={cn(
          'fixed bottom-4 right-4 z-50',
          position === 'bottom-left' && 'left-4 right-auto',
          position === 'inline' && 'relative bottom-auto right-auto'
        )}>
          <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg text-sm text-gray-600 dark:text-gray-400 max-w-xs">
            <div className="flex items-center gap-2">
              <AlertCircle size={16} />
              <span>AI Chat: {accessData.reason.replace('_', ' ')}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  // Chat Widget Toggle Button
  const ChatToggleButton = () => {
    return (
      <Button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
        }}
        className={cn(
          'fixed z-50 rounded-full w-14 h-14 shadow-lg transition-all duration-200 hover:scale-110',
          brandColors.primary,
          position === 'bottom-right' ? 'bottom-6 right-6' : 'bottom-6 left-6'
        )}
        data-testid="button-chat-toggle"
        title="Open customer support chat"
      >
        <MessageCircle className="h-6 w-6" />
      </Button>
    );
  };

  // Inline chat (for embedding in pages)
  if (position === 'inline') {
    return (
      <Card className={cn("w-full max-w-md mx-auto", className)}>
        <CardHeader className={cn("pb-3", brandColors.secondary)}>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Bot className="h-4 w-4" />
            {brandInfo.name} Support
          </CardTitle>
          <p className="text-xs opacity-80">{brandInfo.supportText}</p>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-96 p-4">
            <ChatMessages messages={messages} isTyping={isTyping} brandColors={brandColors} />
          </ScrollArea>
          <ChatInput 
            inputMessage={inputMessage}
            setInputMessage={setInputMessage}
            onSend={sendMessage}
            onKeyPress={handleKeyPress}
            isLoading={sendMessageMutation.isPending}
            brandColors={brandColors}
          />
        </CardContent>
      </Card>
    );
  }

  // Floating chat widget
  return (
    <>
      {!isOpen && <ChatToggleButton />}
      
      {isOpen && (
        <Card className={cn(
          "fixed z-50 w-80 h-96 shadow-2xl transition-all duration-300",
          position === 'bottom-right' ? 'bottom-6 right-6' : 'bottom-6 left-6',
          className
        )}>
          <CardHeader className={cn("pb-3 flex-row items-center justify-between", brandColors.secondary)}>
            <div>
              <CardTitle className="flex items-center gap-2 text-sm">
                <Bot className="h-4 w-4" />
                {brandInfo.name} Support
              </CardTitle>
              <p className="text-xs opacity-80">{brandInfo.supportText}</p>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsOpen(false);
                }}
                className="h-8 w-8 p-0 hover:bg-white/20 text-gray-600 hover:text-gray-800"
                data-testid="button-chat-close"
                title="Close chat"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          
          <CardContent className="p-0 flex flex-col h-full">
            <ScrollArea className="flex-1 p-4">
              <ChatMessages messages={messages} isTyping={isTyping} brandColors={brandColors} />
            </ScrollArea>
            <ChatInput 
              inputMessage={inputMessage}
              setInputMessage={setInputMessage}
              onSend={sendMessage}
              onKeyPress={handleKeyPress}
              isLoading={sendMessageMutation.isPending}
              brandColors={brandColors}
            />
          </CardContent>
        </Card>
      )}
    </>
  );
}

// Chat Messages Component
function ChatMessages({ messages, isTyping, brandColors }: {
  messages: ChatMessage[];
  isTyping: boolean;
  brandColors: any;
}) {
  return (
    <div className="space-y-4">
      {messages.map((message) => (
        <div
          key={message.id}
          className={cn(
            "flex gap-2",
            message.role === 'user' ? 'justify-end' : 'justify-start'
          )}
        >
          {message.role === 'assistant' && (
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
              <Bot className="h-3 w-3" />
            </div>
          )}
          
          <div
            className={cn(
              "max-w-xs px-3 py-2 rounded-lg text-sm",
              message.role === 'user'
                ? cn(brandColors.primary, "text-white")
                : "bg-gray-100 text-gray-900"
            )}
          >
            <p className="whitespace-pre-wrap">{message.content}</p>
            {message.category && (
              <Badge variant="secondary" className="mt-1 text-xs">
                {message.category}
              </Badge>
            )}
            {message.needsEscalation && (
              <div className="flex items-center gap-1 mt-2 text-xs opacity-75">
                <AlertCircle className="h-3 w-3" />
                Escalating to human agent
              </div>
            )}
          </div>
          
          {message.role === 'user' && (
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
              <User className="h-3 w-3 text-white" />
            </div>
          )}
        </div>
      ))}
      
      {isTyping && (
        <div className="flex gap-2 justify-start">
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
            <Bot className="h-3 w-3" />
          </div>
          <div className="bg-gray-100 px-3 py-2 rounded-lg">
            <div className="flex space-x-1">
              <div className="w-1 h-1 bg-gray-500 rounded-full animate-bounce"></div>
              <div className="w-1 h-1 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
              <div className="w-1 h-1 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Chat Input Component
function ChatInput({ 
  inputMessage, 
  setInputMessage, 
  onSend, 
  onKeyPress, 
  isLoading,
  brandColors 
}: {
  inputMessage: string;
  setInputMessage: (value: string) => void;
  onSend: () => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  isLoading: boolean;
  brandColors: any;
}) {
  return (
    <div className="p-3 border-t">
      <div className="flex gap-2">
        <Input
          value={inputMessage}
          onChange={(e) => {
            setInputMessage(e.target.value);
          }}
          onKeyPress={onKeyPress}
          placeholder="Type your message..."
          disabled={isLoading}
          className="flex-1"
          data-testid="input-chat-message"
          autoComplete="off"
        />
        <Button
          onClick={onSend}
          disabled={isLoading || !inputMessage.trim()}
          size="sm"
          className={brandColors.primary}
          data-testid="button-chat-send"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}